import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Check, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const formatSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(0)} MB` : "");

const SPOTIFY_ARTIST_URL = "https://open.spotify.com/artist/06NV3L7olz3ocY4pe5GUFZ";

const SpotifyIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm5.5 17.3a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.33-1.46c4.57-1.05 8.5-.6 11.66 1.34.35.21.46.67.25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 1 1-.54-1.8c4.36-1.32 9.78-.68 13.49 1.6.44.27.58.85.31 1.29zm.13-3.4C15.26 8.33 8.9 8.12 5.2 9.24a1.12 1.12 0 1 1-.65-2.15c4.25-1.29 11.3-1.04 15.75 1.6a1.12 1.12 0 0 1-1.15 1.94z" />
  </svg>
);

export const RemixDownloadDialog = ({ remix, onOpenChange }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(null);
  const [followed, setFollowed] = useState(false);

  useEffect(() => {
    if (remix) { setReady(null); setFollowed(false); }
  }, [remix]);

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url.startsWith("/api") ? `${BACKEND}${url}` : url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!remix) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/remixes/${remix.id}/download`, { email });
      setReady(res.data);
      toast.success("You're on the list", { description: "One last step: follow on Spotify to unlock." });
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) toast.error("Too many requests. Please try again later.");
      else if (status === 422) toast.error("Please enter a valid email address.");
      else toast.error("Couldn't start the download. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollow = () => {
    window.open(SPOTIFY_ARTIST_URL, "_blank", "noopener,noreferrer");
    setFollowed(true);
    if (ready) {
      triggerDownload(ready.url, ready.filename);
      toast.success("Download started", { description: "Thanks for following." });
    }
  };

  if (!remix) return null;

  const shareUrl = `${window.location.origin}/remix/${remix.slug || remix.id}`;

  const handleShare = async () => {
    const shareData = {
      title: `${remix.title} — Alchemist ${remix.remix_type}`,
      text: `Free download: ${remix.original_artist ? `${remix.original_artist} — ` : ""}${remix.title} (Alchemist ${remix.remix_type})`,
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch (err) { if (err?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied", { description: shareUrl });
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  return (
    <Dialog open={!!remix} onOpenChange={onOpenChange}>
      <DialogContent data-testid="remix-download-dialog" className="bg-[#0a0a0a] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tighter leading-tight">
            {remix.title}
          </DialogTitle>
          <DialogDescription className="font-sub text-[10px] text-white/40 tracking-widest mt-2 uppercase">
            {remix.original_artist ? `${remix.original_artist} · ` : ""}Alchemist {remix.remix_type}
            {remix.audio_size ? ` · ${formatSize(remix.audio_size)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {ready ? (
          <div data-testid="remix-download-ready" className="mt-6 space-y-5">
            <div className="flex items-center gap-3 text-white/50">
              <Check className="w-4 h-4" />
              <p className="font-sub text-[10px] tracking-widest uppercase">Step 1 · Email saved</p>
            </div>
            <div className="border border-white/10 p-5 space-y-4">
              <p className="font-sub text-[10px] tracking-widest uppercase text-white/50">
                Step 2 · {followed ? "Following" : "Follow to unlock"}
              </p>
              <p className="font-body text-sm text-white/60">
                {followed
                  ? "Your download has started. Thanks for the support."
                  : "Follow Alchemist on Spotify to unlock the free download."}
              </p>
              <button
                type="button"
                data-testid="remix-spotify-follow-btn"
                onClick={handleFollow}
                className="w-full bg-[#1DB954] hover:bg-[#1ed760] text-black py-4 uppercase tracking-widest text-[10px] font-sub transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <SpotifyIcon className="w-4 h-4" />
                {followed ? "Open Spotify again" : "Follow on Spotify & Download"}
              </button>
            </div>
            <button
              type="button"
              data-testid="remix-download-again-btn"
              disabled={!followed}
              onClick={() => triggerDownload(ready.url, ready.filename)}
              className="btn-alchemist w-full border border-white/25 bg-white/5 hover:bg-white hover:text-black py-4 uppercase tracking-widest text-[10px] font-sub transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-30 disabled:hover:bg-white/5 disabled:hover:text-white disabled:cursor-not-allowed"
            >
              <Download className="w-3 h-3" />
              {followed ? "Didn't start? Download again" : "Download locked"}
            </button>
            <button
              type="button"
              data-testid="remix-share-btn-ready"
              onClick={handleShare}
              className="w-full py-2 uppercase tracking-widest text-[10px] font-sub text-white/40 hover:text-white transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Share2 className="w-3 h-3" />
              Share this remix
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            <p className="font-body text-white/40 text-sm">
              Two quick steps to unlock the free download: drop your email, then follow Alchemist on Spotify.
            </p>
            <div>
              <Label className="font-sub text-[10px] text-white/40 tracking-widest mb-3 block">EMAIL</Label>
              <Input
                data-testid="remix-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-white placeholder:text-white/15 focus-visible:ring-0 focus-visible:border-white"
                placeholder="your@email.com"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="remix-download-submit"
              disabled={submitting}
              className="btn-alchemist w-full border border-white/25 bg-white/5 hover:bg-white hover:text-black py-4 uppercase tracking-widest text-[10px] font-sub transition-colors duration-300 disabled:opacity-30 flex items-center justify-center gap-2"
            >
              <Download className="w-3 h-3" />
              {submitting ? "Preparing..." : "Continue"}
            </button>
            <button
              type="button"
              data-testid="remix-share-btn"
              onClick={handleShare}
              className="w-full py-2 uppercase tracking-widest text-[10px] font-sub text-white/40 hover:text-white transition-colors duration-300 flex items-center justify-center gap-2"
            >
              <Share2 className="w-3 h-3" />
              Share link
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
